/* =========================================================
   Lobster Box theme script
   - Location memory, delivery-date rules, Shopify cart drawer
   - Shared header behaviour and page transitions
   - All the GSAP animation
   window.LB is set in layout/theme.liquid
   ========================================================= */
(() => {
gsap.registerPlugin(ScrollTrigger, Draggable, InertiaPlugin);
const designMode = !!(window.Shopify && Shopify.designMode);
const reduce = designMode || matchMedia('(prefers-reduced-motion: reduce)').matches;
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];
const store = {
  get(k, d) { try { const v = sessionStorage.getItem(k); return v ? JSON.parse(v) : d; } catch (e) { return d; } },
  set(k, v) { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
};
const LB = window.LB || {};
const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
const page = document.body.dataset.page;

/* ---------------- money ---------------- */
function money(cents) {
  const fmt = LB.moneyFormat || '£{{amount}}';
  const v = (cents / 100);
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => {
    if (k === 'amount_no_decimals') return Math.round(v).toLocaleString('en-GB');
    if (k === 'amount_with_comma_separator') return v.toFixed(2).replace('.', ',');
    const s = v.toFixed(2);
    return s.endsWith('.00') ? s.slice(0, -3) : s;
  });
}

/* ---------------- locations & delivery rules (from theme settings) ---------------- */
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ORD = ['', '1st', '2nd', '3rd', '4th', '5th'];
// YYYY-MM-DD in the visitor's own time zone (toISOString would use UTC and can slip a day late at night)
const ymd = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const LOCATIONS = {};
// Fully booked / closed dates, typed by the owner in Theme settings, one per line or separated by commas.
// Understands 16/10/2026, 16/10/26, 2026-10-16, 16 Oct 2026, Fri 16 October (no year = the next one coming up)
// and runs of dates like "20/12/2026 - 02/01/2027" or "20 Dec to 2 Jan".
const MONTH_NAMES = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
function parseOneDate(s) {
  s = s.trim().toLowerCase().replace(/(\d)(st|nd|rd|th)\b/g, '$1').replace(/^(mon|tue|wed|thu|fri|sat|sun)[a-z]*,?\s*/, '');
  let y, m, d, m1;
  if ((m1 = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) [, y, m, d] = m1.map(Number);
  else if ((m1 = s.match(/^(\d{1,2})[\/.-](\d{1,2})(?:[\/.-](\d{2,4}))?$/))) { d = +m1[1]; m = +m1[2]; y = m1[3] ? +m1[3] : null; }
  else if ((m1 = s.match(/^(\d{1,2})\s+([a-z]+)\.?(?:\s+(\d{2,4}))?$/)) && MONTH_NAMES.includes(m1[2].slice(0, 3))) { d = +m1[1]; m = MONTH_NAMES.indexOf(m1[2].slice(0, 3)) + 1; y = m1[3] ? +m1[3] : null; }
  else return null;
  if (y !== null && y < 100) y += 2000;
  if (y === null) {   // no year given: this year, or next year if that date has already gone
    const now = new Date(); y = now.getFullYear();
    if (new Date(y, m - 1, d, 23) < now) y++;
  }
  const date = new Date(y, m - 1, d, 12);
  return date.getMonth() === m - 1 && date.getDate() === d ? date : null;
}
function parseDateList(text) {
  const out = new Set();
  String(text || '').split(/[\n,;]+/).map(s => s.trim()).filter(Boolean).forEach(item => {
    const [a, b] = item.split(/\s+(?:-|–|to|until)\s+/i);
    const from = parseOneDate(a), to = b ? parseOneDate(b) : from;
    if (!from || !to) { console.warn('Lobster Box: could not read the date "' + item + '" in Theme settings → Delivery rules'); return; }
    for (const d = new Date(from); d <= to && out.size < 2000; d.setDate(d.getDate() + 1)) out.add(ymd(d));
  });
  return out;
}
const CLOSED = parseDateList(LB.closed);
Object.entries(LB.rules || {}).forEach(([key, r]) => {
  const days = String(r.days || '').split(/[,&]|\band\b/i).map(s => DAY_NAMES.findIndex(d => d.toLowerCase().startsWith(s.trim().toLowerCase().slice(0, 3)))).filter(i => i >= 0 && String(r.days).trim());
  const weeks = String(r.weeks || '').split(',').map(n => parseInt(n, 10)).filter(n => n >= 1 && n <= 5);
  const dayList = days.map(d => DAY_NAMES[d]).join(' and ');
  const daysText = !days.length ? 'on your chosen date'
    : weeks.length ? `on the ${weeks.map(w => ORD[w]).join(' and ')} ${dayList} of every month`
    : days.length === 1 ? `every ${dayList}` : dayList;
  const full = parseDateList(r.full); CLOSED.forEach(k => full.add(k));
  // extra one-off delivery dates (e.g. Christmas), on top of the usual days
  const extra = parseDateList(r.extra);
  LOCATIONS[key] = {key, name: r.name, days, weeks: weeks.length ? weeks : null, daysText, radius: +r.radius || 0, centre: String(r.centre || '').trim(), full, extra};
});

// remember which location homepage the visitor came through (for this visit only)
if (LB.location && LOCATIONS[LB.location]) store.set('lb-loc', {key: LB.location, url: location.pathname});
const saved = store.get('lb-loc', null);
const LOC = saved && LOCATIONS[saved.key] ? LOCATIONS[saved.key] : null;

function earliestDate() {
  // orders must be in by 12pm, 48 hours before delivery
  const d = new Date();
  d.setDate(d.getDate() + (d.getHours() >= 12 ? 3 : 2));
  return ymd(d);
}
function validDay(d, loc) {
  if (loc.extra && loc.extra.has(ymd(d))) return true;
  if (loc.days.length && !loc.days.includes(d.getDay())) return false;
  if (loc.weeks && !loc.weeks.includes(Math.ceil(d.getDate() / 7))) return false;
  return true;
}
// a delivery day that hasn't been marked fully booked / closed
const isFull = (d, loc) => loc.full.has(ymd(d));
const openDay = (d, loc) => validDay(d, loc) && !isFull(d, loc);
function nextDates(loc, n = 2) {
  const d = new Date(earliestDate() + 'T12:00'), out = [];
  for (let i = 0; i < 400 && out.length < n; i++, d.setDate(d.getDate() + 1)) {
    if (openDay(d, loc)) out.push(d.toLocaleDateString('en-GB', {weekday: 'short', day: 'numeric', month: 'short'}));
  }
  return out.join(' and ');
}
function checkDate(value, loc) {
  if (!value) return 'Please choose a delivery date.';
  if (value < earliestDate()) return 'Orders must be placed by 12pm, 48 hours before your delivery date.';
  if (!validDay(new Date(value + 'T12:00'), loc)) {
    const where = `We deliver ${loc.key === 'nationwide' ? 'nationwide' : 'in ' + loc.name} ${loc.daysText}.`;
    return loc.weeks ? `${where} Next runs: ${nextDates(loc)}.` : `${where} Please pick a ${loc.days.map(d => DAY_NAMES[d]).join(' or ')}.`;
  }
  if (isFull(new Date(value + 'T12:00'), loc)) return `Sorry, we're fully booked in ${loc.name} on that date. Next available: ${nextDates(loc)}.`;
  return '';
}

/* ---------------- delivery calendar ----------------
   Only the delivery days for the chosen area can be picked; every other day is greyed out. */
const MONTHS_AHEAD = 12;
function deliveryCalendar({input, button, panel, getLoc, onPick}) {
  const label = button.querySelector('span');
  const firstAvailable = () => {
    const d = new Date(earliestDate() + 'T12:00');
    for (let i = 0; i < 400; i++, d.setDate(d.getDate() + 1)) if (openDay(d, getLoc())) return new Date(d);
    return new Date(earliestDate() + 'T12:00');
  };
  const limit = new Date(); limit.setMonth(limit.getMonth() + MONTHS_AHEAD);
  let view = firstAvailable(); view.setDate(1);

  function render() {
    const loc = getLoc(), earliest = earliestDate(), today = new Date();
    const year = view.getFullYear(), month = view.getMonth();
    const startPad = (new Date(year, month, 1).getDay() + 6) % 7;   // weeks start on Monday
    const days = new Date(year, month + 1, 0).getDate();
    let cells = '', anyFull = false, anyExtra = false;
    for (let i = 0; i < startPad; i++) cells += '<span></span>';
    for (let n = 1; n <= days; n++) {
      const d = new Date(year, month, n, 12), key = ymd(d);
      const inRange = key >= earliest && d <= limit && validDay(d, loc), full = inRange && isFull(d, loc), ok = inRange && !full;
      if (full) anyFull = true;
      const extra = ok && loc.extra && loc.extra.has(key); if (extra) anyExtra = true;
      cells += `<button type="button" data-day="${key}" class="${ok ? 'ok' : ''}${extra ? ' extra' : ''}${full ? 'booked' : ''}${key === input.value ? ' sel' : ''}" ${ok ? '' : 'disabled'} ${full ? 'title="Fully booked"' : ''} aria-label="${d.toLocaleDateString('en-GB', {weekday: 'long', day: 'numeric', month: 'long'})}${ok ? '' : full ? ' (fully booked)' : ' (not available)'}">${n}</button>`;
    }
    const atStart = year === today.getFullYear() && month === today.getMonth();
    const atEnd = year === limit.getFullYear() && month === limit.getMonth();
    panel.innerHTML = `
      <div class="cal-head">
        <button type="button" data-nav="-1" aria-label="Previous month" ${atStart ? 'disabled' : ''}>‹</button>
        <b>${view.toLocaleDateString('en-GB', {month: 'long', year: 'numeric'})}</b>
        <button type="button" data-nav="1" aria-label="Next month" ${atEnd ? 'disabled' : ''}>›</button>
      </div>
      <div class="cal-grid">${['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(w => `<span class="dow">${w}</span>`).join('')}${cells}</div>
      <p class="cal-key"><i></i> ${loc.name}: we deliver ${loc.daysText}</p>
      ${anyExtra ? '<p class="cal-key"><i class="extra"></i> Extra festive delivery day</p>' : ''}
      ${anyFull ? '<p class="cal-key"><i class="booked"></i> Fully booked</p>' : ''}`;
  }
  const open = () => { render(); panel.hidden = false; button.setAttribute('aria-expanded', 'true'); if (!reduce) gsap.fromTo(panel, {opacity: 0, y: -8}, {opacity: 1, y: 0, duration: .3}); };
  const close = () => { panel.hidden = true; button.setAttribute('aria-expanded', 'false'); };
  button.addEventListener('click', () => panel.hidden ? open() : close());
  panel.addEventListener('click', e => {
    const nav = e.target.closest('[data-nav]');
    if (nav) { view.setMonth(view.getMonth() + +nav.dataset.nav); render(); return; }
    const day = e.target.closest('[data-day]');
    if (day && !day.disabled) {
      input.value = day.dataset.day;
      label.textContent = new Date(input.value + 'T12:00').toLocaleDateString('en-GB', {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'});
      button.classList.add('chosen');
      close(); onPick();
    }
  });
  // (composedPath, because clicking a month arrow re-draws the panel and the clicked button is gone by now)
  document.addEventListener('click', e => { const path = e.composedPath(); if (!panel.hidden && !path.includes(panel) && !path.includes(button)) close(); });
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && !panel.hidden) { close(); button.focus(); } });
  return {
    // when the delivery area changes: clear a date that's no longer valid and jump to the first available month
    refresh() {
      if (input.value && checkDate(input.value, getLoc())) { input.value = ''; label.textContent = 'Choose a delivery date'; button.classList.remove('chosen'); }
      view = input.value ? new Date(input.value + 'T12:00') : firstAvailable(); view.setDate(1);
      if (!panel.hidden) render();
    }
  };
}

/* ---------------- header bits that depend on location ---------------- */
function fillLocation() {
  $$('[data-home-link]').forEach(a => a.href = saved ? saved.url : root);
  $$('[data-loc-label]').forEach(el => el.innerHTML = LOC ? `${LOC.name}<span>· change</span>` : 'Choose location');
  $$('[data-loc-name]').forEach(el => el.textContent = LOC ? LOC.name : 'you');
  $$('[data-next-dates]').forEach(el => { const l = LOCATIONS[el.dataset.nextDates]; if (l) el.textContent = nextDates(l); });
  $$('[data-days-text]').forEach(el => { const l = LOCATIONS[el.dataset.daysText]; if (l) el.textContent = l.daysText; });
  // shop, search and "more boxes": hide boxes that aren't sold in the visitor's chosen area
  // (location homepages already only list their own area's boxes)
  if (LOC) $$('[data-grid]').forEach(grid => {
    const cards = $$('.card', grid);
    cards.forEach(c => { if (c.dataset.areas && !c.dataset.areas.split(',').includes(LOC.key)) c.remove(); });
    if (cards.length && !$('.card', grid)) grid.insertAdjacentHTML('beforeend', `<p class="empty-note">New boxes for ${LOC.name} are coming soon.</p>`);
  });
}

/* ---------------- Shopify cart ---------------- */
let cart = LB.cart || {items: [], total_price: 0, item_count: 0};
const api = (url, body) => fetch(root + url.replace(/^\//, ''), body ? {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)} : {headers: {Accept: 'application/json'}})
  .then(async r => {
    // read as text first: Shopify sometimes replies with an empty body (e.g. on preview links), which isn't valid JSON
    const text = await r.text(); let j = {};
    try { j = text ? JSON.parse(text) : {}; } catch (e) { j = {}; }
    if (!r.ok) {
      // Shopify sometimes sends the reason as an object (e.g. gift card checks): turn it into a plain sentence
      const err = j.errors || (typeof j.description === 'object' ? j.description : null) || {};
      let msg = typeof j.description === 'string' ? j.description : '';
      if (err.email) msg = 'Please check the recipient\'s email address.';
      else if (err.send_on) msg = 'The send date must be within 90 days of today.';
      else if (!msg && j.message) msg = String(j.message).replace(/^Validation failed:\s*/i, '');
      throw new Error(msg || 'Something went wrong. Please refresh the page and try again.');
    }
    return j;
  });
const esc = s => String(s).replace(/[&<>"]/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'}[c]));
function renderCart(bump) {
  $$('.cart-count').forEach(el => el.textContent = cart.item_count);
  const items = $('.drawer .items'); if (!items) return;
  items.innerHTML = cart.items.length ? cart.items.map(i => {
    const opts = [i.variant_title && i.variant_title !== 'Default Title' ? i.variant_title : '', ...Object.entries(i.properties || {}).filter(([k, v]) => v && !k.startsWith('_')).map(([k, v]) => v)].filter(Boolean);
    return `<div class="citem"><img src="${i.image ? i.image + (i.image.includes('?') ? '&' : '?') + 'width=160' : ''}" alt=""><div><b>${esc(i.product_title)}</b>
      <small>${opts.map(esc).join(' · ')}</small>
      <span class="q"><button type="button" data-key="${i.key}" data-qty="${i.quantity - 1}" aria-label="One fewer">−</button>${i.quantity}<button type="button" data-key="${i.key}" data-qty="${i.quantity + 1}" aria-label="One more">+</button></span>
      <button class="rm" data-key="${i.key}" data-qty="0" type="button">Remove</button></div><span>${money(i.final_line_price)}</span></div>`;
  }).join('') : '<p class="empty">Your basket is empty, like a lobster pot at low tide.</p>';
  $('.drawer .sum').textContent = money(cart.total_price);
  const free = (LB.freeDelivery || 0) * 100, left = free - cart.total_price;
  if (free) {
    $('.freetext').innerHTML = left > 0 ? `Spend <b>${money(left)}</b> more for <b>free delivery</b>` : '<b>You have free delivery</b> 🦞';
    gsap.to('.freebar .bar i', {scaleX: Math.min(cart.total_price / free, 1), duration: reduce ? 0 : .8, ease: 'power3.out'});
  }
  $('.drawer .checkout').disabled = !cart.items.length;
  if (bump && !reduce) gsap.fromTo('.cart-btn', {scale: 1.25}, {scale: 1, duration: .6, ease: 'elastic.out(1,.4)'});
  document.dispatchEvent(new CustomEvent('lb:cart', {detail: cart}));   // the Shop page listens for this
}

/* ---------------- starters & desserts ("extras") ----------------
   Extras are delivered with a Lobster Box, so they take the box's delivery area and date,
   and the basket can't check out with extras alone. Extras = Product type "Starter", "Dessert", or anything containing "Main" (main course add-ons). */
const isExtra = i => { const t = String(i.product_type || '').trim(); return /^(starter|dessert)s?$/i.test(t) || /main/i.test(t); };
const hasBox = c => (c.items || []).some(i => !isExtra(i) && !i.gift_card);
function syncExtras(c) {
  const box = (c.items || []).find(i => !isExtra(i) && (i.properties || {})['Delivery date']);
  if (!box) return Promise.resolve(c);
  const bp = box.properties;
  const todo = c.items.filter(i => isExtra(i) && ((i.properties || {})['Delivery date'] !== bp['Delivery date'] || (i.properties || {})['Delivery area'] !== bp['Delivery area']));
  if (!todo.length) return Promise.resolve(c);
  const props = i => Object.assign({}, i.properties || {}, {'Delivery area': bp['Delivery area'], 'Delivery date': bp['Delivery date'], '_With box': box.product_title});
  return todo.reduce((p, i) => p.then(() => api('/cart/change.js', {id: i.key, quantity: i.quantity, properties: props(i)})), Promise.resolve())
    .then(() => api('/cart.js'));
}
// used by the Shop page's "Add to box" buttons (assets/byb.js)
window.LBCart = {
  get: () => cart,
  add: id => api('/cart/add.js', {items: [{id: +id, quantity: 1}]}).then(() => api('/cart.js')).then(syncExtras)
    .then(c => { if (c.items) { cart = c; renderCart(true); } return cart; }),
  remove: id => {
    const line = (cart.items || []).find(i => String(i.variant_id) === String(id));
    if (!line) return Promise.resolve(cart);
    return api('/cart/change.js', {id: line.key, quantity: 0}).then(() => api('/cart.js')).then(c => { if (c.items) { cart = c; renderCart(); } return cart; });
  }
};
// no checking out with only starters or desserts in the basket
document.addEventListener('submit', e => {
  const sub = e.submitter;
  if (!sub || sub.name !== 'checkout') return;
  const items = cart.items || [];
  if (items.some(isExtra) && !hasBox(cart)) {
    e.preventDefault();
    let n = e.target.querySelector('.extra-warn');
    if (!n) { n = document.createElement('p'); n.className = 'extra-warn'; n.style.cssText = 'color:var(--lobster);font-size:.85rem;line-height:1.5;margin:0 0 12px'; sub.insertAdjacentElement('beforebegin', n); }
    n.textContent = 'Starters, main course add-ons and desserts are delivered with your Lobster Box. Please add a box to your basket first.';
  }
}, true);
const refreshCart = bump => api('/cart.js').then(c => { if (c.items) { cart = c; renderCart(bump); } });
const openCart = () => { gsap.to('.drawer', {xPercent: 0, duration: reduce ? 0 : .6, ease: 'expo.out'}); gsap.to('.shade', {opacity: 1, pointerEvents: 'auto', duration: .4}); };
const closeCart = () => { gsap.to('.drawer', {xPercent: 100, duration: reduce ? 0 : .5, ease: 'expo.in'}); gsap.to('.shade', {opacity: 0, pointerEvents: 'none', duration: .4}); };
function flyToCart(fromImg, done) {
  const btn = $('.cart-btn');
  if (reduce || !fromImg || !btn) return done();
  const from = fromImg.getBoundingClientRect(), to = btn.getBoundingClientRect(), ghost = fromImg.cloneNode();
  Object.assign(ghost.style, {position: 'fixed', zIndex: 120, left: from.left + 'px', top: from.top + 'px', width: from.width + 'px', height: from.height + 'px', objectFit: 'cover', borderRadius: '10px', pointerEvents: 'none'});
  document.body.appendChild(ghost);
  gsap.to(ghost, {left: to.left, top: to.top, width: 30, height: 30, borderRadius: '50%', duration: .8, ease: 'power3.in', onComplete: () => { ghost.remove(); done(); }});
}

/* ---------------- product page ---------------- */
function initProduct() {
  const wrap = $('[data-product-form]'); if (!wrap) return;
  const product = JSON.parse($('[data-product-json]').textContent);
  const form = $('form', wrap), area = $('#area'), date = $('#date'), hint = $('#dateHint'), qty = $('#qty'), btn = $('[data-add]', wrap), priceEl = $('[data-price]');
  // delivery areas
  const dateBtn = $('#dateBtn');
  if (area) {
    // only the areas this box is sold in (from its Cornwall / London / Nationwide tags; no tags = every area)
    const tagged = String(wrap.dataset.areas || '').split(',').filter(k => LOCATIONS[k]);
    const allowed = tagged.length ? Object.values(LOCATIONS).filter(l => tagged.includes(l.key)) : Object.values(LOCATIONS);
    const want = LOC && allowed.some(l => l.key === LOC.key) ? LOC.key : allowed.some(l => l.key === 'nationwide') ? 'nationwide' : allowed[0].key;
    area.innerHTML = allowed.map(l => `<option value="${l.key}" ${l.key === want ? 'selected' : ''}>${l.name}</option>`).join('');
    const areaHint = $('#areaHint');
    if (areaHint && tagged.length) {
      const names = allowed.map(l => l.name).join(' and ');
      areaHint.hidden = false;
      areaHint.classList.toggle('warn', !!(LOC && !tagged.includes(LOC.key)));
      areaHint.textContent = LOC && !tagged.includes(LOC.key)
        ? `Sorry, this box isn't available for ${LOC.name} delivery. It's only available for ${names}.`
        : `Available for ${names} delivery only.`;
    }
  }
  const setHint = msg => {
    if (!hint) return;
    if (!area) { hint.textContent = msg || ''; hint.classList.toggle('warn', !!msg); return; }   // gift cards: no delivery questions
    const l = LOCATIONS[area.value];
    hint.textContent = msg || `${l.name}: we deliver ${l.daysText}${l.weeks ? ` (next: ${nextDates(l)})` : ''}. Order by 12pm, 48 hours before.`;
    hint.classList.toggle('warn', !!msg);
  };
  if (area) {
    setHint();
    const cal = deliveryCalendar({input: date, button: dateBtn, panel: $('#cal'), getLoc: () => LOCATIONS[area.value], onPick: () => setHint(checkDate(date.value, LOCATIONS[area.value]))});
    area.addEventListener('change', () => { cal.refresh(); setHint(); });
  }
  // What3Words: asked for Cornwall and London, where our own team delivers
  const w3wField = $('#w3wField'), w3w = $('#w3w'), w3wHint = $('#w3wHint');
  const w3wTip = w3wHint ? w3wHint.innerHTML : '';
  const showW3w = () => { if (w3wField && area) w3wField.hidden = !['cornwall', 'london'].includes(area.value); };
  if (area) { showW3w(); area.addEventListener('change', showW3w); }
  // accepts "///filled.count.soap", "filled.count.soap" or a what3words.com link; returns null if it doesn't look right
  const cleanW3w = s => {
    const m = s.trim().toLowerCase().replace(/^https?:\/\/(www\.)?(what3words\.com|w3w\.co)\//, '').replace(/^\/+/, '').match(/^([a-zÀ-ɏ]+)[.\s]+([a-zÀ-ɏ]+)[.\s]+([a-zÀ-ɏ]+)$/);
    return m ? `///${m[1]}.${m[2]}.${m[3]}` : null;
  };
  // Delivery radius (London): the customer's postcode must be within the set number of miles of the centre postcode.
  // Postcodes are looked up with postcodes.io, a free UK postcode service.
  const pcField = $('#pcField'), pc = $('#pc'), pcHint = $('#pcHint');
  const lookups = {};
  const lookup = code => lookups[code] || (lookups[code] = fetch('https://api.postcodes.io/postcodes/' + encodeURIComponent(code))
    .then(r => r.status === 404 ? null : r.ok ? r.json().then(j => j.result) : Promise.reject(new Error('lookup failed')))
    .catch(e => { delete lookups[code]; throw e; }));
  const miles = (a, b) => {
    const R = 3958.8, rad = d => d * Math.PI / 180, dLat = rad(b.latitude - a.latitude), dLng = rad(b.longitude - a.longitude);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  };
  const tidyPc = s => { const c = s.toUpperCase().replace(/[^A-Z0-9]/g, ''); return c.length > 4 ? c.slice(0, -3) + ' ' + c.slice(-3) : c; };
  const pcMsg = (text, warn, html) => { if (!pcHint) return; pcHint[html ? 'innerHTML' : 'textContent'] = text; pcHint.classList.toggle('warn', !!warn); };
  // returns {ok, postcode}; ok is false when the order can't go ahead yet (the hint says why)
  const checkPostcode = async () => {
    const l = area && LOCATIONS[area.value];
    if (!pcField || !l || !l.radius) return {ok: true};
    const code = tidyPc(pc.value);
    if (!code) { pcMsg(`Please enter the postcode we're delivering to, so we can check it's on our ${l.name} run.`, true); return {ok: false}; }
    if (!/^[A-Z]{1,2}[0-9][A-Z0-9]? [0-9][A-Z]{2}$/.test(code)) { pcMsg('That doesn\'t look like a full UK postcode. Please check it, e.g. SW1A 1AA.', true); return {ok: false}; }
    pc.value = code; pcMsg('Checking your postcode…');
    try {
      const [home, centre] = await Promise.all([lookup(code), lookup(tidyPc(l.centre))]);
      if (!home) { pcMsg('We couldn\'t find that postcode. Please check it and try again.', true); return {ok: false}; }
      if (!centre) { pcMsg(''); return {ok: true, postcode: code}; }   // centre postcode not set up correctly: don't block customers
      const d = miles(home, centre);
      if (d <= l.radius) { pcMsg(`Great, ${code} is on our ${l.name} run.`); return {ok: true, postcode: code}; }
      pcMsg(`Sorry, ${code} is about ${d < 20 ? d.toFixed(1) : Math.round(d)} miles from central ${l.name}, beyond our ${l.name} run (up to ${l.radius} miles). We can send your box by DHL Before 12 instead. <button type="button" class="linkbtn" data-to-nationwide>Switch to Nationwide delivery</button>`, true, true);
      return {ok: false};
    } catch (e) {
      // the postcode service is unreachable: let the order through and check it by hand
      pcMsg(''); return {ok: true, postcode: code};
    }
  };
  const showPc = () => { if (!pcField || !area) return; const l = LOCATIONS[area.value]; pcField.hidden = !(l && l.radius); if (pcField.hidden) pcMsg(''); else if (pc.value.trim()) checkPostcode(); };
  if (area && pcField) {
    showPc(); area.addEventListener('change', showPc);
    pc.addEventListener('change', checkPostcode);
    pcHint.addEventListener('click', e => {
      if (!e.target.closest('[data-to-nationwide]') || !LOCATIONS.nationwide) return;
      area.value = 'nationwide'; area.dispatchEvent(new Event('change', {bubbles: true}));
    });
  }
  // gift cards: optionally emailed straight to someone else (Shopify's own gift card recipient fields)
  const giftSend = $('#giftSend'), giftFields = $('#giftFields');
  if (giftSend) giftSend.addEventListener('change', () => { giftFields.hidden = !giftSend.checked; if (giftSend.checked) $('#giftEmail').focus(); });
  const giftOn = $('#giftOn');
  if (giftOn) { const d = new Date(), m = new Date(); m.setDate(m.getDate() + 90); giftOn.min = ymd(d); giftOn.max = ymd(m); }
  // optional printed card, posted (a hidden £5 add-on product added alongside the gift card)
  const giftPrint = $('#giftPrint'), giftPrintFields = $('#giftPrintFields');
  if (giftPrint) giftPrint.addEventListener('change', () => { giftPrintFields.hidden = !giftPrint.checked; });
  // variant picking from the option pills
  const current = () => {
    const chosen = product.options.map((o, i) => (form.querySelector(`input[name="option${i}"]:checked`) || {}).value);
    return product.variants.find(v => v.options.every((o, i) => o === chosen[i]));
  };
  const update = () => {
    const v = current();
    if (priceEl) priceEl.textContent = v ? money(v.price) : '';
    btn.disabled = !v || !v.available;
    btn.textContent = !v ? 'Unavailable' : v.available ? 'Add to basket' : 'Sold out';
  };
  form.addEventListener('change', e => { if (e.target.name && e.target.name.startsWith('option')) update(); });
  update();
  $$('[data-q]', form).forEach(b => b.addEventListener('click', () => qty.textContent = Math.max(1, +qty.textContent + +b.dataset.q)));
  // gallery thumbs
  $$('.thumbs button').forEach(b => b.addEventListener('click', () => {
    $$('.thumbs button').forEach(x => x.classList.toggle('on', x === b));
    const img = $('.pgallery .main img');
    gsap.to(img, {opacity: 0, scale: 1.04, duration: .25, onComplete: () => { img.src = b.dataset.src; img.removeAttribute('srcset'); gsap.to(img, {opacity: 1, scale: 1, duration: .5}); }});
  }));
  form.addEventListener('submit', async e => {
    e.preventDefault();
    const v = current(); if (!v) return;
    const properties = {};
    if (area) {
      const l = LOCATIONS[area.value], err = checkDate(date.value, l);
      if (err) { setHint(err); if (!reduce) gsap.fromTo(dateBtn, {x: -8}, {x: 0, duration: .5, ease: 'elastic.out(1,.3)'}); dateBtn.focus(); return; }
      properties['Delivery area'] = l.name;
      properties['Delivery date'] = new Date(date.value + 'T12:00').toLocaleDateString('en-GB', {weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'});
      if (pcField && !pcField.hidden) {
        btn.disabled = true; const pcOk = await checkPostcode(); btn.disabled = false;
        if (!pcOk.ok) { pc.focus(); return; }
        if (pcOk.postcode) properties['Delivery postcode'] = pcOk.postcode;
      }
      if (w3w && !w3wField.hidden && w3w.value.trim()) {
        const words = cleanW3w(w3w.value);
        if (!words) { w3wHint.textContent = 'That doesn\'t look like a What3Words address. It should be three words with dots, like ///filled.count.soap'; w3wHint.classList.add('warn'); w3w.focus(); return; }
        w3wHint.innerHTML = w3wTip; w3wHint.classList.remove('warn');
        properties['What3Words'] = words;
      }
    }
    if (giftSend && giftSend.checked) {
      const email = $('#giftEmail').value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setHint('Please enter the email address to send the gift card to.'); $('#giftEmail').focus(); return; }
      properties['__shopify_send_gift_card_to_recipient'] = 'on';
      properties['Recipient email'] = email;
      if ($('#giftName').value.trim()) properties['Recipient name'] = $('#giftName').value.trim();
      if ($('#giftMsg').value.trim()) properties['Message'] = $('#giftMsg').value.trim();
      const sendOn = $('#giftOn').value;
      if (sendOn && (sendOn < $('#giftOn').min || sendOn > $('#giftOn').max)) { setHint('The send date must be between today and 90 days from now.'); $('#giftOn').focus(); return; }
      if (sendOn) properties['Send on'] = sendOn;
      setHint('');
    }
    const dessert = form.querySelector('input[name="dessert"]:checked');
    if (dessert) properties['Dessert'] = dessert.value;
    btn.disabled = true;
    // one delivery area per order: checkout only takes one delivery address
    if (properties['Delivery area']) {
      const now = await api('/cart.js').catch(() => cart);
      const other = (now.items || []).filter(i => !isExtra(i)).map(i => (i.properties || {})['Delivery area']).find(a => a && a !== properties['Delivery area']);
      if (other) {
        btn.disabled = false;
        hint.innerHTML = `Your basket already has a box for <b>${esc(other)}</b> delivery, and each order can only go to one area. Please check out that order first, or <button type="button" class="linkbtn" data-fresh-basket>empty your basket and add this box</button>.`;
        hint.classList.add('warn');
        const fresh = $('[data-fresh-basket]', hint);
        fresh.addEventListener('click', () => api('/cart/clear.js', {}).then(c => { cart = c.items ? c : {items: [], item_count: 0, total_price: 0}; renderCart(); setHint(); form.requestSubmit ? form.requestSubmit() : btn.click(); }), {once: true});
        return;
      }
    }
    const items = [{id: v.id, quantity: +qty.textContent, properties}];
    if (giftPrint && giftPrint.checked) {
      const note = $('#giftCardNote').value.trim(), addonProps = {'For': product.title};
      if (note) addonProps['Message for the card'] = note;
      items.push({id: +giftPrint.dataset.addon, quantity: +qty.textContent, properties: addonProps});
    }
    api('/cart/add.js', {items})
      .then(() => api('/cart.js'))
      .then(c => {
        // make sure the box really is in the basket before saying so
        if (!c.items || !c.items.some(i => i.variant_id === v.id)) throw new Error('Sorry, we couldn\'t add that to your basket. Please refresh the page and try again.');
        return c;
      })
      // starters and desserts already in the basket follow this box's delivery date
      .then(syncExtras)
      // update the basket straight away (not after the fly-to-basket animation, which can lag on a busy phone)
      .then(c => { cart = c; renderCart(true); flyToCart($('.pgallery .main img'), openCart); })
      .catch(err => setHint(err.message))
      .finally(() => { btn.disabled = false; });
  });
}

/* ---------------- split headings into words ---------------- */
function splitText() {
  $$('.split').forEach(el => {
    el.innerHTML = el.innerHTML.split(/(<br\s*\/?>|<em[^>]*>|<\/em>|<p>|<\/p>|\s+)/).map(t => (!t.trim() || t.startsWith('<')) ? t : `<span class="w"><span class="wi">${t}</span></span>`).join('');
  });
  $$('.scrub').forEach(el => { el.innerHTML = el.textContent.trim().split(/\s+/).map(w => `<span class="sw">${esc(w)}</span>`).join(' '); });
}

/* ---------------- interactions ---------------- */
function bindUI() {
  $$('.cart-btn').forEach(b => b.addEventListener('click', openCart));
  $('.drawer .close')?.addEventListener('click', closeCart);
  $('.shade')?.addEventListener('click', closeCart);
  $('.drawer .items')?.addEventListener('click', e => {
    const b = e.target.closest('[data-key]'); if (!b) return;
    api('/cart/change.js', {id: b.dataset.key, quantity: +b.dataset.qty}).then(c => c.items ? c : api('/cart.js')).then(c => { if (c.items) { cart = c; renderCart(); } });
  });

  let menuOpen = false;
  const toggleMenu = () => {
    menuOpen = !menuOpen;
    const menu = $('.menu');
    if (menuOpen) {
      gsap.set(menu, {display: 'flex'});
      gsap.fromTo(menu, {clipPath: 'inset(0 0 100% 0)'}, {clipPath: 'inset(0 0 0% 0)', duration: .6, ease: 'expo.out'});
      if (!reduce) gsap.from('.menu a span', {yPercent: 110, stagger: .05, duration: .7, ease: 'expo.out', delay: .1});
    } else {
      gsap.to(menu, {clipPath: 'inset(0 0 100% 0)', duration: .45, ease: 'expo.in', onComplete: () => gsap.set(menu, {display: 'none'})});
    }
    gsap.to('.burger i:first-child', {rotation: menuOpen ? 45 : 0, y: menuOpen ? 4 : 0, duration: .3});
    gsap.to('.burger i:last-child', {rotation: menuOpen ? -45 : 0, y: menuOpen ? -4 : 0, duration: .3});
  };
  $('.burger')?.addEventListener('click', toggleMenu);

  const lb = $('.lightbox'), lbImg = lb && $('img', lb);
  const closeLightbox = () => { if (lb && lb.style.display === 'grid') gsap.to(lb, {opacity: 0, duration: .3, onComplete: () => gsap.set(lb, {display: 'none'})}); };
  document.addEventListener('click', e => {
    const z = e.target.closest('.zoomable'); if (!z || !lb) return;
    const img = $('img', z);
    lbImg.src = img.currentSrc || img.src; lbImg.alt = img.alt;
    gsap.set(lb, {display: 'grid'});
    gsap.fromTo(lb, {opacity: 0}, {opacity: 1, duration: .3});
    gsap.fromTo(lbImg, {scale: .88, y: 30}, {scale: 1, y: 0, duration: .7, ease: 'expo.out'});
  });
  lb?.addEventListener('click', closeLightbox);
  document.addEventListener('keydown', e => { if (e.key === 'Escape') { closeCart(); closeLightbox(); if (menuOpen) toggleMenu(); } });

  $$('.acc button').forEach(b => b.addEventListener('click', () => {
    const acc = b.parentElement, open = !acc.classList.contains('open');
    acc.classList.toggle('open', open);
    b.setAttribute('aria-expanded', open);
    gsap.to($('.panel', acc), {height: open ? 'auto' : 0, duration: reduce ? 0 : .5, ease: 'power3.inOut', onComplete: () => ScrollTrigger.refresh()});
  }));

  $$('.tabs').forEach(tabs => {
    const btns = $$('button', tabs), blob = $('.blob', tabs), scope = tabs.closest('section') || document;
    const show = (b, instant) => {
      btns.forEach(x => x.classList.toggle('on', x === b));
      gsap.to(blob, {x: b.offsetLeft, width: b.offsetWidth, top: b.offsetTop, height: b.offsetHeight, duration: instant || reduce ? 0 : .5, ease: 'expo.out'});
      $$('.tabpane', scope).forEach(p => p.classList.toggle('on', p.dataset.pane === b.dataset.tab));
      if (!instant && !reduce) gsap.from($$(`.tabpane[data-pane="${b.dataset.tab}"] > *`, scope), {y: 30, opacity: 0, stagger: .06, duration: .6, ease: 'power3.out'});
      ScrollTrigger.refresh();
    };
    btns.forEach(b => b.addEventListener('click', () => show(b)));
    show(btns.find(b => LOC && b.dataset.tab === LOC.key) || btns[0], true);
    addEventListener('resize', () => show($('.on', tabs), true));
  });

  // reviews rotate every 7 seconds; hovering pauses, dots jump to a review
  $$('[data-reviews]').forEach(el => {
    const figs = $$('.rv figure', el), dots = $$('.dots button', el);
    if (figs.length < 2) return;
    let cur = 0, paused = false;
    gsap.set(figs, {autoAlpha: 0}); gsap.set(figs[0], {autoAlpha: 1});
    const show = n => {
      if (n === cur) return;
      gsap.to(figs[cur], {autoAlpha: 0, y: reduce ? 0 : -20, duration: reduce ? 0 : .5, ease: 'power2.in'});
      gsap.fromTo(figs[n], {autoAlpha: 0, y: reduce ? 0 : 20}, {autoAlpha: 1, y: 0, duration: reduce ? 0 : .7, delay: reduce ? 0 : .45, ease: 'power3.out'});
      dots.forEach((d, i) => d.classList.toggle('on', i === n));
      cur = n;
      timer.restart(true);
    };
    const timer = gsap.delayedCall(7, () => { if (!paused) show((cur + 1) % figs.length); else timer.restart(true); });
    dots.forEach((d, i) => d.addEventListener('click', () => show(i)));
    el.addEventListener('mouseenter', () => paused = true);
    el.addEventListener('mouseleave', () => paused = false);
  });

  // (checks the scroll position directly, so the bar stays solid right down to the bottom of the page)
  ScrollTrigger.create({onUpdate: s => $('.nav')?.classList.toggle('solid', page !== 'home' || s.scroll() > 60)});
  if (page !== 'home') $('.nav')?.classList.add('solid');

  Draggable.create('.float', {bounds: 'body', inertia: true});
  buildPool();
  $$('.strip').forEach(strip => {
    const track = $('.strip-track', strip);
    Draggable.create(track, {type: 'x', inertia: true, edgeResistance: .8, bounds: {minX: 0, maxX: 0},
      onPress() { this.applyBounds({minX: Math.min(0, strip.offsetWidth - track.scrollWidth), maxX: 0}); }});
  });
}

/* ---------------- rock pool ---------------- */
let critters = [];
function buildPool() {
  $$('.pool').forEach(pool => {
    const A = LB.assets || {};
    const list = [
      ['scallop', 150, 8, 10], ['starfish', 200, 76, 6], ['brittlestar', 160, 84, 60], ['scallopGlow', 240, 3, 62],
      ['scallop', 110, 40, 76], ['starfish', 120, 62, 30], ['brittlestar', 120, 20, 36], ['scallop', 95, 70, 80],
      ['scallop', 80, 50, 6], ['starfish', 95, 90, 34], ['scallop', 120, 26, 82]
    ];
    const made = list.map(([name, size, left, top]) => {
      const c = document.createElement('div');
      c.className = 'critter';
      c.style.cssText = `width:min(${size}px,${(size / 8).toFixed(1)}vw);left:${left}%;top:${top}%`;
      c.innerHTML = `<div class="repel"><img src="${A[name]}" alt="" draggable="false"></div>`;
      pool.appendChild(c);
      const repel = c.firstElementChild, img = repel.firstElementChild;
      Draggable.create(c, {
        bounds: pool, inertia: true, zIndexBoost: true,
        onPress: () => gsap.to(img, {scale: 1.15, duration: .3}),
        onRelease: () => gsap.to(img, {scale: 1, duration: .6, ease: 'elastic.out(1,.4)'}),
        onClick: () => gsap.to(repel, {rotation: '+=360', duration: 1.2, ease: 'back.out(1.6)'})
      });
      return {c, repel, img, pool};
    });
    critters.push(...made);
    pool.addEventListener('mousemove', e => {
      if (reduce) return;
      made.forEach(({c, repel}) => {
        const r = c.getBoundingClientRect(), dx = r.left + r.width / 2 - e.clientX, dy = r.top + r.height / 2 - e.clientY;
        const dist = Math.hypot(dx, dy) || 1, R = 220;
        if (dist < R) {
          const push = (1 - dist / R) * 90;
          gsap.to(repel, {x: dx / dist * push, y: dy / dist * push, duration: .6, ease: 'power3.out', overwrite: 'auto'});
        } else gsap.to(repel, {x: 0, y: 0, duration: 1.4, ease: 'power2.out', overwrite: 'auto'});
      });
    });
  });
}

/* ---------------- page transitions ---------------- */
let leaving = false;
function leave(href) {
  if (reduce) { location.href = href; return; }
  leaving = true;
  gsap.set('.curtain', {display: 'grid', yPercent: 100});
  gsap.to('.curtain', {yPercent: 0, duration: .6, ease: 'expo.inOut', onComplete: () => location.href = href});
}
if (!designMode) document.addEventListener('click', e => {
  const a = e.target.closest('a[href]');
  if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || a.target === '_blank' || a.hasAttribute('download')) return;
  const raw = a.getAttribute('href');
  if (raw.startsWith('#') || raw.startsWith('mailto:') || raw.startsWith('tel:')) return;
  const url = new URL(a.href, location.href);
  if (url.origin !== location.origin || /^\/(checkout|cart|account|apps|admin|tools)/.test(url.pathname.replace(root, '/'))) return;
  if (url.pathname === location.pathname && url.hash) return;
  e.preventDefault();
  leave(url.href);
});
addEventListener('pageshow', e => { if (e.persisted) { leaving = false; gsap.set('.curtain', {yPercent: -100, display: 'none'}); } });

/* ---------------- animation ---------------- */
function animate() {
  const intro = gsap.timeline({paused: true});
  if ($('.hero-bg')) intro.from('.hero-bg', {scale: 1.3, duration: 2, ease: 'expo.out'}, 0);
  intro.from($$('.hero h1 .wi, .page-hero h1 .wi, .landing h1 .wi'), {yPercent: 110, duration: 1.1, stagger: .06, ease: 'expo.out'}, 0.1)
       .from($$('.intro'), {y: 24, opacity: 0, duration: .9, stagger: .1, ease: 'power3.out'}, 0.5);
  if (page === 'home' && $('.nav')) intro.from('.nav', {y: -80, duration: .8, ease: 'power3.out'}, 0.4);
  if (page === 'landing') intro.from('.panel-loc', {yPercent: 30, opacity: 0, duration: 1.1, stagger: .12, ease: 'expo.out'}, 0.2);

  if (page === 'landing' && $('.loader')) {
    const counter = {v: 0};
    gsap.timeline()
      .to(counter, {v: 100, duration: 1.1, ease: 'power2.inOut', onUpdate: () => $('.loader .count').textContent = Math.round(counter.v)})
      .to('.loader img', {scale: .6, opacity: 0, duration: .4}, '-=.2')
      .to('.loader', {clipPath: 'inset(0 0 100% 0)', duration: .9, ease: 'expo.inOut'})
      .set('.loader', {display: 'none'})
      .add(() => intro.play(), '-=.6');
    gsap.set('.curtain', {display: 'none'});
  } else {
    gsap.to('.curtain', {yPercent: -100, duration: .8, ease: 'expo.inOut', delay: .1, onComplete: () => gsap.set('.curtain', {display: 'none'})});
    // safety net on very slow devices, where animations crawl: never leave the page covered for more than 2.5 seconds
    setTimeout(() => { if (!leaving) gsap.set('.curtain', {display: 'none'}); }, 2500);
    gsap.delayedCall(.45, () => intro.play());
  }

  if ($('.hero')) {
    gsap.to('.hero-bg', {yPercent: 18, ease: 'none', scrollTrigger: {trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true}});
    gsap.to('.hero-copy', {yPercent: -30, opacity: 0, ease: 'none', scrollTrigger: {trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true}});
  }
  $$('.marquee-track').forEach(t => {
    const mq = gsap.to(t, {xPercent: -50, duration: 26, ease: 'none', repeat: -1});
    ScrollTrigger.create({onUpdate: s => { gsap.to(mq, {timeScale: 1 + Math.min(Math.abs(s.getVelocity()) / 300, 5), duration: .2, overwrite: true}); gsap.to(mq, {timeScale: 1, duration: 1, delay: .2}); }});
  });
  $$('.split').forEach(el => {
    if (el.closest('.hero, .page-hero, .landing')) return;
    gsap.from($$('.wi', el), {yPercent: 110, duration: 1, stagger: .05, ease: 'expo.out', scrollTrigger: {trigger: el, start: 'top 88%'}});
  });
  $$('.reveal').forEach(el => gsap.from(el, {y: 50, opacity: 0, duration: 1, ease: 'power3.out', scrollTrigger: {trigger: el, start: 'top 88%'}}));
  $$('.reveal-stagger, [data-grid]').forEach(el => gsap.from(el.children, {y: 60, opacity: 0, duration: 1, stagger: .12, ease: 'power3.out', scrollTrigger: {trigger: el, start: 'top 85%'}}));
  $$('.scrub').forEach(el => gsap.to($$('.sw', el), {opacity: 1, stagger: .1, ease: 'none', scrollTrigger: {trigger: el, start: 'top 80%', end: 'bottom 45%', scrub: true}}));
  $$('[data-parallax]').forEach(img => gsap.to(img, {yPercent: +img.dataset.parallax, ease: 'none', scrollTrigger: {trigger: img.parentElement, start: 'top bottom', end: 'bottom top', scrub: true}}));
  $$('.wipe').forEach(el => gsap.from(el, {clipPath: 'inset(0 100% 0 0)', duration: 1.4, ease: 'expo.inOut', scrollTrigger: {trigger: el, start: 'top 75%'}}));
  $$('.float').forEach((f, i) => {
    if (f.dataset.spin) return gsap.to(f, {rotation: +f.dataset.spin, duration: Math.abs(+f.dataset.spin) / 3, repeat: -1, ease: 'none'});
    gsap.to(f, {yPercent: gsap.utils.random(-12, 12), rotation: gsap.utils.random(-10, 10), duration: gsap.utils.random(4, 6), yoyo: true, repeat: -1, ease: 'sine.inOut', delay: i * .3});
  });
  $$('.pool').forEach(pool => {
    const imgs = critters.filter(o => o.pool === pool).map(o => o.img);
    gsap.from(imgs, {scale: 0, rotation: -90, duration: 1.2, stagger: .07, ease: 'back.out(1.7)', scrollTrigger: {trigger: pool, start: 'top 70%'}});
    imgs.forEach((img, i) => gsap.to(img, {yPercent: gsap.utils.random(-12, 12), rotation: gsap.utils.random(-15, 15), duration: gsap.utils.random(3, 6), yoyo: true, repeat: -1, ease: 'sine.inOut', delay: i * .2}));
  });

  const mm = gsap.matchMedia();
  $$('.journey').forEach(journey => {
    mm.add('(min-width: 761px)', () => {
      const track = $('.track', journey), dist = () => track.scrollWidth - innerWidth;
      const tween = gsap.to(track, {x: () => -dist(), ease: 'none', scrollTrigger: {trigger: journey, pin: true, scrub: 1, end: () => '+=' + dist(), invalidateOnRefresh: true}});
      gsap.to($('.progress i', journey), {scaleX: 1, ease: 'none', scrollTrigger: {trigger: journey, start: 'top top', end: () => '+=' + dist(), scrub: true}});
      $$('.step', journey).forEach(step => {
        gsap.to($('img', step), {scale: 1, ease: 'none', scrollTrigger: {trigger: step, containerAnimation: tween, start: 'left right', end: 'right left', scrub: true}});
        gsap.from($('.num', step), {yPercent: 80, opacity: 0, duration: .8, ease: 'back.out(2)', scrollTrigger: {trigger: step, containerAnimation: tween, start: 'left 75%'}});
      });
    });
    mm.add('(max-width: 760px)', () => $$('.step', journey).forEach(step => gsap.from(step, {y: 60, opacity: 0, duration: .9, scrollTrigger: {trigger: step, start: 'top 85%'}})));
  });
  $$('.band').forEach(band => {
    gsap.to($('.frame', band), {clipPath: 'inset(0% 0% round 0px)', ease: 'none', scrollTrigger: {trigger: band, start: 'top 85%', end: 'top top', scrub: true}});
    gsap.to($('.frame img', band), {yPercent: -12, ease: 'none', scrollTrigger: {trigger: band, start: 'top bottom', end: 'bottom top', scrub: true}});
  });
  mm.add('(min-width: 761px)', () => {
  });
  $$('.gcol .zoomable').forEach(f => gsap.from(f, {opacity: 0, y: 60, duration: 1, ease: 'power3.out', scrollTrigger: {trigger: f, start: 'top 92%'}}));
  $$('.sticky-story').forEach(story => {
    const imgs = $$('.stack img', story);
    $$('.chapter', story).forEach((ch, i) => ScrollTrigger.create({trigger: ch, start: 'top 55%', end: 'bottom 55%', onToggle: s => {
      if (!s.isActive) return;
      imgs.forEach((img, j) => gsap.to(img, {opacity: j === i ? 1 : 0, scale: j === i ? 1 : 1.08, duration: .9, ease: 'power2.out', overwrite: true}));
    }}));
  });
  // timeline (our story): the red line draws down as you scroll; each photo opens up and its words rise in
  $$('.timeline').forEach(tl => {
    const line = $('.tl-line i', tl);
    if (line) gsap.to(line, {scaleY: 1, ease: 'none', scrollTrigger: {trigger: tl, start: 'top 60%', end: 'bottom 60%', scrub: true}});
    $$('.tl-item', tl).forEach(item => {
      gsap.timeline({scrollTrigger: {trigger: item, start: 'top 82%'}})
        .from($('.tl-pic', item), {clipPath: 'inset(14% 14% 14% 14% round 10px)', duration: 1.3, ease: 'expo.out'}, 0)
        .from($('.tl-pic img', item), {scale: 1.2, duration: 1.6, ease: 'expo.out'}, 0)
        .from($('.tl-dot', item), {scale: 0, duration: .6, ease: 'back.out(3)'}, .1)
        .from($$('.tl-txt > *', item), {y: 30, opacity: 0, stagger: .1, duration: .9, ease: 'power3.out'}, .2);
    });
  });
  $$('[data-count]').forEach(el => {
    const o = {v: 0};
    gsap.to(o, {v: +el.dataset.count, duration: 2, ease: 'power2.out', onUpdate: () => el.textContent = Math.round(o.v), scrollTrigger: {trigger: el, start: 'top 88%'}});
  });
  if ($('.waves')) gsap.to('.waves', {xPercent: -50, duration: 10, repeat: -1, ease: 'none'});

  document.addEventListener('mousemove', e => {
    const card = e.target.closest('.tilt'); if (!card) return;
    const r = card.getBoundingClientRect(), x = (e.clientX - r.left) / r.width - .5, y = (e.clientY - r.top) / r.height - .5;
    gsap.to(card, {rotationY: x * 10, rotationX: -y * 10, transformPerspective: 900, duration: .5});
  });
  document.addEventListener('mouseout', e => {
    const card = e.target.closest('.tilt');
    if (card && !card.contains(e.relatedTarget)) gsap.to(card, {rotationX: 0, rotationY: 0, duration: .8, ease: 'elastic.out(1,.5)'});
  });
  $$('.magnetic').forEach(b => {
    b.addEventListener('mousemove', e => { const r = b.getBoundingClientRect(); gsap.to(b, {x: (e.clientX - r.left - r.width / 2) * .3, y: (e.clientY - r.top - r.height / 2) * .4, duration: .4}); });
    b.addEventListener('mouseleave', () => gsap.to(b, {x: 0, y: 0, duration: .7, ease: 'elastic.out(1,.4)'}));
  });
  if (page === 'landing') {
    mm.add('(min-width: 761px)', () => {
      const panels = $$('.panel-loc');
      panels.forEach(p => {
        p.addEventListener('mouseenter', () => panels.forEach(q => { gsap.to(q, {flexGrow: q === p ? 1.8 : .7, duration: .8, ease: 'expo.out'}); gsap.to($('img', q), {scale: q === p ? 1 : 1.1, duration: 1.2, ease: 'expo.out'}); }));
        p.addEventListener('mouseleave', () => panels.forEach(q => { gsap.to(q, {flexGrow: 1, duration: .8, ease: 'expo.out'}); gsap.to($('img', q), {scale: 1.1, duration: 1.2, ease: 'expo.out'}); }));
      });
    });
  }
}

/* ---------------- start ---------------- */
if (page === 'landing' && 'scrollRestoration' in history) { history.scrollRestoration = 'manual'; scrollTo(0, 0); }
fillLocation();
if (!designMode) splitText();
gsap.set('.drawer', {xPercent: 100});
renderCart();
initProduct();
bindUI();
if (reduce) {
  gsap.set('.curtain, .loader', {display: 'none'});
  gsap.set('.sw', {opacity: 1});
  gsap.set('.band .frame', {clipPath: 'none'});
  $$('.sticky-story .stack img').forEach((img, i) => img.style.opacity = i ? 0 : 1);
} else {
  // don't wait more than 1.5s for the fonts before starting
  Promise.race([document.fonts.ready, new Promise(r => setTimeout(r, 1500))]).then(animate);
}
window.LB_READY = true;
// keep the basket in step if the visitor comes back via the back button
addEventListener('pageshow', e => { if (e.persisted) refreshCart(); });
})();
